<template>
  <a-modal
    :visible="visible"
    :confirm-loading="loading"
    :footer="null"
    width="800px"
    @cancel="handleCancel"
  >
    <template #title>
      <div class="modal-title">결제 내역</div>
    </template>
    
    <div class="modal-content">
      <div class="hospital-info">
        <div class="info-item">
          <span class="label">병원명:</span>
          <span class="value">{{ subscription?.hospitalName || '-' }}</span>
        </div>
        <div class="info-item">
          <span class="label">상품명:</span>
          <span class="value">{{ subscription?.productName || '-' }}</span>
        </div>
        <div class="info-item">
          <span class="label">계약기간:</span>
          <span class="value">{{ formatDate(subscription?.contractStartDate) }} ~ {{ formatDate(subscription?.contractEndDate) }}</span>
        </div>
      </div>
      
      <a-divider />
      
      <div v-if="paymentHistories.length === 0" class="empty-payment">
        <a-empty description="결제 내역이 없습니다." />
      </div>
      
      <div v-else class="payment-table-wrapper">
        <a-table
          :dataSource="paymentHistories"
          :columns="columns"
          :pagination="false"
          bordered
          size="small"
          :scroll="tableScroll"
        >
          <template #bodyCell="{ column, text, record }">
            <!-- 결제 상태 -->
            <template v-if="column.dataIndex === 'paymentStatus'">
              <a-tag :color="getPaymentStatusColor(text)">{{ getPaymentStatusText(text) }}</a-tag>
            </template>
            
            <!-- 결제 방법 -->
            <template v-if="column.dataIndex === 'paymentMethod'">
              {{ getPaymentMethodText(text) }}
            </template>
            
            <!-- 정기 결제일 -->
            <template v-if="column.dataIndex === 'regularPaymentDay'">
              {{ text || '-' }}
            </template>
            
            <!-- 실제 결제일 -->
            <template v-if="column.dataIndex === 'actualPaymentDay'">
              {{ text || '-' }}
            </template>
            
            <!-- 결제 금액 -->
            <template v-if="column.dataIndex === 'regularPaymentAmount'">
              {{ formatCurrency(text) }}
            </template>
            
            <!-- 영수증 -->
            <template v-if="column.dataIndex === 'receiptUrl'">
              <a-button 
                v-if="text" 
                type="link" 
                size="small" 
                @click="openReceiptUrl(text)"
              >
                영수증
              </a-button>
              <span v-else>-</span>
            </template>
            
            <!-- 실패사유 -->
            <template v-if="column.dataIndex === 'failReason'">
              <a-tooltip v-if="text" :title="text">
                <div class="truncate-text">{{ text }}</div>
              </a-tooltip>
              <span v-else>-</span>
            </template>
          </template>
        </a-table>
      </div>
      
      <div class="modal-footer">
        <a-button @click="handleCancel" class="close-button">닫기</a-button>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { ref, computed, watch, nextTick } from 'vue';
import dayjs from 'dayjs';

export default {
  name: 'PaymentHistoryModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    subscription: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['close', 'update:visible'],
  setup(props, { emit }) {
    const loading = ref(false);
    const tableScroll = ref(null);
    
    // 결제 이력 데이터
    const paymentHistories = computed(() => {
      if (!props.subscription?.paymentHistory) return [];
      
      return props.subscription.paymentHistory.map((item, index) => ({
        ...item,
        key: index,
        index: index + 1
      })).sort((a, b) => b.paymentRound - a.paymentRound); // 최근 회차가 위로 오도록 정렬
    });
    
    // 테이블 열 정의
    const columns = [
      { 
        title: '회차', 
        dataIndex: 'paymentRound', 
        key: 'paymentRound',
        align: 'center', 
        width: 70 
      },
      { 
        title: '상태', 
        dataIndex: 'paymentStatus', 
        key: 'paymentStatus', 
        align: 'center', 
        width: 100 
      },
      { 
        title: '결제 방법', 
        dataIndex: 'paymentMethod', 
        key: 'paymentMethod', 
        align: 'center', 
        width: 120 
      },
      { 
        title: '예정 결제일', 
        dataIndex: 'regularPaymentDay', 
        key: 'regularPaymentDay', 
        align: 'center', 
        width: 120 
      },
      { 
        title: '실제 결제일', 
        dataIndex: 'actualPaymentDay', 
        key: 'actualPaymentDay', 
        align: 'center', 
        width: 120 
      },
      { 
        title: '금액', 
        dataIndex: 'regularPaymentAmount', 
        key: 'regularPaymentAmount', 
        align: 'right', 
        width: 120 
      },
      { 
        title: '영수증', 
        dataIndex: 'receiptUrl', 
        key: 'receiptUrl', 
        align: 'center', 
        width: 80 
      },
      { 
        title: '실패사유', 
        dataIndex: 'failReason', 
        key: 'failReason', 
        align: 'left', 
        width: 200 
      }
    ];
    
    // 결제 상태에 따른 색상 가져오기
    const getPaymentStatusColor = (status) => {
      switch (status) {
        case 'COMPLETED': return 'green';
        case 'PENDING': return 'blue';
        case 'FAILED': return 'red';
        case 'REFUNDED': return 'orange';
        case 'CANCELED': return 'gray';
        default: return 'default';
      }
    };
    
    // 결제 상태 텍스트 가져오기
    const getPaymentStatusText = (status) => {
      switch (status) {
        case 'COMPLETED': return '완료';
        case 'PENDING': return '대기';
        case 'FAILED': return '실패';
        case 'REFUNDED': return '환불';
        case 'CANCELED': return '취소';
        default: return '-';
      }
    };
    
    // 결제 방법 텍스트 가져오기
    const getPaymentMethodText = (method) => {
      switch (method) {
        case 'CREDIT_CARD': return '신용카드';
        case 'BANK_TRANSFER': return '계좌이체';
        case 'VIRTUAL_ACCOUNT': return '가상계좌';
        default: return '-';
      }
    };
    
    // 화폐 형식으로 포맷팅
    const formatCurrency = (amount) => {
      if (amount === undefined || amount === null) return '-';
      return `${amount.toLocaleString()}원`;
    };
    
    // 날짜 형식화 함수
    const formatDate = (timestamp) => {
      if (!timestamp) return '-';
      return dayjs(timestamp).format('YYYY-MM-DD');
    };
    
    // 영수증 URL 열기
    const openReceiptUrl = (url) => {
      if (!url) return;
      window.open(url, '_blank');
    };
    
    // 모달 닫기 핸들러
    const handleCancel = () => {
      emit('update:visible', false);
      emit('close');
    };
    
    // visible prop 변경 감지
    watch(() => props.visible, (newVal) => {
      if (newVal) {
        // 모달이 열릴 때 스크롤 설정 지연 적용 (ResizeObserver 오류 방지)
        tableScroll.value = null;
        nextTick(() => {
          setTimeout(() => {
            tableScroll.value = { x: 700 };
          }, 100);
        });
      }
    });
    
    return {
      loading,
      paymentHistories,
      columns,
      getPaymentStatusColor,
      getPaymentStatusText,
      getPaymentMethodText,
      formatCurrency,
      formatDate,
      openReceiptUrl,
      handleCancel,
      tableScroll
    };
  }
};
</script>

<style scoped lang="scss">
.modal-title {
  font-size: 16px;
  font-weight: bold;
}

.modal-content {
  padding: 20px;
  background: #fff;
  border: none;
}

.hospital-info {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
}

.info-item {
  flex: 1;
  min-width: 200px;
  
  .label {
    font-weight: 500;
    color: #666;
    margin-right: 8px;
  }
  
  .value {
    font-weight: 600;
    color: #000;
  }
}

.empty-payment {
  display: flex;
  justify-content: center;
  padding: 40px 0;
}

.payment-table-wrapper {
  margin-bottom: 20px;
}

.truncate-text {
  max-width: 180px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
}

:deep(.yellow-button) {
  background-color: #FFCC32 !important;
  border-color: #FFCC32 !important;
  color: #000 !important;
  
  &:hover {
    background-color: #E6B82E !important;
    border-color: #E6B82E !important;
  }
  
  &:disabled {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
  }
}
</style>
